// <auto-generated>
// This file is automatically generated for policy configuration.
// Do not modify this file directly. Changes will be overwritten.
// Generated: 2025-12-23T10:09:18.818Z
// </auto-generated>

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/apiClient';

export interface PolicySetDto {
  id: string;
  tenantId?: string;
  orgUnitId?: string;
  policyType: string;
  entityName: string;
  isActive: boolean;
  version: number;
  rules: PolicyRuleDto[];
}

export interface PolicyRuleDto {
  id: string;
  policySetId: string;
  transitionKey: string;
  fieldPath: string;
  isRequired: boolean;
  condition?: string;
  message?: string;
}

export interface FieldCatalogItemDto {
  path: string;
  label: string;
  section?: string;
  type: string;
}

export interface TransitionDto {
  key: string;
  from: string;
  to: string;
  description?: string;
}

export interface GetPolicySetsRequest {
  tenantId?: string;
  orgUnitId?: string;
  entityName?: string;
  policyType?: string;
  skipCount?: number;
  maxResultCount?: number;
}

export interface CreateOrUpdatePolicySetDto {
  id?: string;
  tenantId?: string;
  orgUnitId?: string;
  policyType: string;
  entityName: string;
  isActive: boolean;
  rules: Array<{
    transitionKey: string;
    fieldPath: string;
    isRequired: boolean;
    condition?: string;
    message?: string;
  }>;
}

/**
 * Hook for managing policy configuration
 */
export function usePolicyConfiguration() {
  const queryClient = useQueryClient();

  // Get policy sets
  const getPolicySets = (request: GetPolicySetsRequest) => {
    return useQuery({
      queryKey: ['policy-sets', request],
      queryFn: async () => {
        const response = await apiClient.get('/api/core/policy-configuration/policy-sets', {
          params: request,
        });
        return response.data;
      },
    });
  };

  // Get policy set by ID
  const getPolicySet = (id: string) => {
    return useQuery({
      queryKey: ['policy-set', id],
      queryFn: async () => {
        const response = await apiClient.get(`/api/core/policy-configuration/policy-set/${id}`);
        return response.data;
      },
      enabled: !!id,
    });
  };

  // Get field catalog
  const getFieldCatalog = (entityName: string) => {
    return useQuery({
      queryKey: ['field-catalog', entityName],
      queryFn: async () => {
        const response = await apiClient.get(`/api/core/policy-configuration/field-catalog`, {
          params: { entityName },
        });
        return response.data;
      },
      enabled: !!entityName,
    });
  };

  // Get transitions
  const getTransitions = (entityName: string) => {
    return useQuery({
      queryKey: ['transitions', entityName],
      queryFn: async () => {
        const response = await apiClient.get(`/api/core/policy-configuration/transitions`, {
          params: { entityName },
        });
        return response.data;
      },
      enabled: !!entityName,
    });
  };

  // Get supported entities
  const getSupportedEntities = () => {
    return useQuery({
      queryKey: ['policy-supported-entities'],
      queryFn: async () => {
        const response = await apiClient.get('/api/core/policy-configuration/supported-entities');
        return response.data as string[];
      },
    });
  };

  // Create or update policy set
  const createOrUpdatePolicySet = useMutation({
    mutationFn: async (dto: CreateOrUpdatePolicySetDto) => {
      const response = await apiClient.post('/api/core/policy-configuration/policy-set', dto);
      return response.data;
    },
    onSuccess: (saved: PolicySetDto) => {
      queryClient.invalidateQueries({ queryKey: ['policy-sets'] });
      // The detail view stays open after a save: refetch it so the form re-seeds from server truth.
      queryClient.invalidateQueries({ queryKey: ['policy-set', saved.id] });
    },
  });

  // Delete policy set
  const deletePolicySet = useMutation({
    mutationFn: async (id: string) => {
      await apiClient.delete(`/api/core/policy-configuration/policy-set/${id}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['policy-sets'] });
    },
  });

  return {
    getPolicySets,
    getPolicySet,
    getSupportedEntities,
    getFieldCatalog,
    getTransitions,
    createOrUpdatePolicySet: createOrUpdatePolicySet.mutate,
    createOrUpdatePolicySetError: createOrUpdatePolicySet.error,
    deletePolicySet: deletePolicySet.mutate,
    deletePolicySetError: deletePolicySet.error,
    isSaving: createOrUpdatePolicySet.isPending,
    isDeleting: deletePolicySet.isPending,
  };
}
